import { ILinkProps, P2GBox, P2GFade, P2GGrid, P2GLink, P2GLinkGroup } from '@parcel2go/core-ui';
import React, { useCallback, useState, lazy, Suspense, useEffect, useMemo, ReactNode } from 'react';
import { IP2GBasketSection } from '~/hooks/use-basket-section';
import { QuoteAddress, QuoteCountry, QuoteRequirementSection } from '~/lib/types/basket-types';
import { AddressLookup, SavedAddress } from '~/lib/types/edge-api-types';
import { useAuth } from 'react-oidc-context';
import { useAnalytics, useTenant } from '@parcel2go/npm-p2g-app';
import AddressSearch from '~/components/address-details/address/search';
import AddressOverview from '~/components/address-details/address/overview';
import { TranslationNamespaces } from '~/lib/constants';
import { useTranslation } from 'react-i18next';
import { cleanAddressFields, validateAddress } from '~/lib/validation';
import { BasketSections } from '~/lib/types/enums';
import { AlgoliaSearchObjectAddress } from '~/lib/types/algolia-types';
import Loading from '~/components/core/progress/loading';
import Extras from '~/components/extras/extras';
import { DestinationUpsells, OriginUpsells } from '~/lib/types/extra-types';
import { StandardFormWidths } from '~/lib/forms';
import { AddressCustomsFields } from '~/lib/types/customs';
import { AddressView } from '~/lib/types/address-types';
import { PublicSettings } from '~/lib/ui-serverless-settings';

const AdditionalFields = lazy(() => import('~/components/address-details/address/additional-fields'));
const AddressEdit = lazy(() => import('~/components/address-details/address/edit'));
const AddressSaved = lazy(() => import('~/components/address-details/address/saved'));
const CmsContent = React.lazy(() => import('~/components/core/content/cms-content'));

export type AddressProps = {
  type: BasketSections.ORIGIN | BasketSections.DESTINATION;
  section: IP2GBasketSection<QuoteAddress>;
  requirements: QuoteRequirementSection;
  unfilteredRequirementKeys?: { key: string; name: string }[];
  errors: Record<string, string>;
  countries: QuoteCountry[];
  message?: React.ReactNode;
  forceSelection?: boolean;
  disabledFields: string[];
  availableExtras: string[];
  sameAsAbove?: ReactNode;
  signIn?: () => void;
  onUseOriginDetails?: (field: string) => void;
  usableOriginDetails?: string[];
};

export default function Address({
  section,
  countries,
  requirements,
  errors,
  type,
  message,
  signIn,
  forceSelection,
  disabledFields,
  onUseOriginDetails,
  usableOriginDetails,
  unfilteredRequirementKeys,
  availableExtras,
  sameAsAbove
}: AddressProps) {
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const { settings } = useTenant();
  const { track } = useAnalytics();
  const { isAuthenticated } = useAuth();

  const [view, setView] = useState<AddressView>(() => {
    const isVaild = validateAddress(section.data, requirements);
    if (isVaild) {
      return AddressView.Overview;
    }

    if (section.data.postcode && section.data.property && section.data.contactname) {
      return AddressView.Edit;
    }

    return AddressView.Search;
  });

  const showExtrasAndCustoms = [AddressView.Overview, AddressView.Edit].includes(view);
  const canEdit = !forceSelection;

  useEffect(() => {
    if (view === AddressView.Overview && !validateAddress(section.data, requirements)) {
      setView(AddressView.Edit);
    }
  }, [view, section.data, requirements]);

  useEffect(() => {
    track({
      type: 'address_view_changed',
      payload: { view, type }
    });
  }, [view]);

  const onSelectedAddress = useCallback(
    async (address: AddressLookup) => {
      const selected = {
        ...section.data,
        postcode: address.postcode,
        property: address.property && address.property.length > 0 ? address.property : address.organisation,
        street: address.street,
        town: address.town,
        locality: address.locality,
        organisation: address.organisation,
        county: address.county,
        metadata: {
          ...section.data.metadata,
          type: 'LOOKUP',
          id: address.id,
          isDefaultSavedAddress: false,
          deliveryPointSuffix: address.deliveryPointSuffix
        }
      };
      section.update(cleanAddressFields(selected, requirements));
      if (validateAddress(selected, requirements)) {
        setView(AddressView.Overview);
      } else {
        setView(AddressView.Edit);
      }

      track({
        type: 'address_search_result_selected',
        payload: { address, type }
      });
    },
    [requirements, section, track, type]
  );

  const onSelectedSavedAddress = useCallback(
    (address: SavedAddress) => {
      const selected = {
        ...section.data,
        contactname: address.name,
        county: address.county,
        email: address.email,
        phone: address.phone,
        postcode: address.postcode,
        property: address.property,
        street: address.street,
        town: address.town,
        organisation: address.organisation,
        locality: undefined,
        metadata: {
          ...section.data.metadata,
          type: 'SAVED',
          id: address.id,
          basedOnAccount: false,
          isDefaultSavedAddress: !!address.isDefault,
          deliveryPointSuffix: undefined
        }
      };
      section.update(cleanAddressFields(selected, requirements));
      if (validateAddress(selected, requirements)) {
        setView(AddressView.Overview);
      } else {
        setView(AddressView.Edit);
      }

      track({
        type: 'address_saved_address_selected',
        payload: { address, type }
      });
    },
    [requirements, section, track, type]
  );

  const onAutoComplete = useCallback(
    (address: AlgoliaSearchObjectAddress) => {
      const selected = {
        ...section.data,
        contactname: address.name,
        email: address.email,
        phone: address.telephone,
        postcode: address.postcode,
        property: address.property,
        street: address.street,
        town: address.town,
        organisation: address.organisation,
        locality: undefined,
        county: address.county || undefined,
        metadata: {
          ...section.data.metadata,
          type: 'ALGOLIA',
          basedOnAccount: false,
          isDefaultSavedAddress: false,
          deliveryPointSuffix: undefined
        }
      };
      section.update(selected);
      if (validateAddress(selected, requirements)) {
        setView(AddressView.Overview);
      } else {
        setView(AddressView.Edit);
      }

      track({
        type: 'address_saved_suggest_selected',
        payload: { address, type }
      });
    },
    [requirements, section, track, type]
  );

  const onOverviewClick = useCallback(() => {
    if (canEdit) {
      setView(AddressView.Edit);
    }
  }, [canEdit, setView]);

  const links = useMemo(() => {
    const output: ILinkProps[] = [];

    if (view !== AddressView.Search) {
      output.push(
        <P2GLink
          key="ADDRESS_BUTTON_VIEW_SEARCH"
          onClick={() => {
            section.update({
              country: section.data.country,
              email: section.data.email,
              phone: section.data.phone,
              contactname: section.data.contactname,
              taxId: section.data.taxId,
              eori: section.data.eori,
              companyTaxNumber: section.data.companyTaxNumber,
              companyStatus: section.data.companyStatus,
              shop: section.data.shop,
              shopId: section.data.shopId,
              metadata: {
                ...section.data.metadata,
                type: undefined,
                basedOnAccount: false,
                isDefaultSavedAddress: false,
                deliveryPointSuffix: undefined
              }
            });
            setView(AddressView.Search);
          }}
          dataTestId="address-search-view-button"
        >
          {t('ADDRESS_BUTTON_VIEW_SEARCH', {
            defaultValue: view === AddressView.Overview ? 'New Search' : 'Address Search'
          })}
        </P2GLink>
      );
    }

    if (!forceSelection && view !== AddressView.Edit) {
      output.push(
        <P2GLink
          key="ADDRESS_BUTTON_VIEW_EDIT"
          onClick={() => setView(AddressView.Edit)}
          dataTestId="address-manual-view-button"
        >
          {t('ADDRESS_BUTTON_VIEW_EDIT', { defaultValue: view === AddressView.Overview ? 'Edit' : 'Manual Entry' })}
        </P2GLink>
      );
    }

    if (
      !forceSelection &&
      view !== AddressView.Saved &&
      (isAuthenticated || signIn) &&
      settings[PublicSettings.ORDERPROCESS.SIGNINDISABLED] !== 'true'
    ) {
      output.push(
        <P2GLink
          key="ADDRESS_BUTTON_VIEW_SAVED"
          onClick={() => (isAuthenticated ? setView(AddressView.Saved) : signIn && signIn())}
          dataTestId="address-saved-view-button"
        >
          {t('ADDRESS_BUTTON_VIEW_SAVED', { defaultValue: 'Use Saved Address' })}
        </P2GLink>
      );
    }

    return output;
  }, [forceSelection, view, isAuthenticated, t, signIn, section, settings]);

  return (
    <P2GGrid container spacing={2} dataTestId={`${type}-address`}>
      <Suspense>
        <CmsContent
          contentKey={`${type}-address-message`.toLowerCase()}
          wrapper={(x) => (
            <P2GGrid item xs={12}>
              {x}
            </P2GGrid>
          )}
          children={message}
        />
      </Suspense>
      {sameAsAbove && (
        <P2GGrid item xs={12}>
          {sameAsAbove}
        </P2GGrid>
      )}
      <P2GGrid item {...StandardFormWidths}>
        <Suspense fallback={<Loading />}>
          {view === AddressView.Search && (
            <P2GFade in>
              <AddressSearch
                type={type}
                disabledFields={disabledFields}
                section={section}
                requirements={requirements}
                errors={errors}
                availableExtras={availableExtras}
                onSelected={onSelectedAddress}
                onAutoComplete={onAutoComplete}
                countries={countries}
                forceSelection={forceSelection}
              />
            </P2GFade>
          )}
          {view === AddressView.Overview && (
            <P2GFade in>
              <AddressOverview
                onEdit={onOverviewClick}
                canEdit={canEdit}
                type={type}
                disabledFields={disabledFields}
                section={section}
                requirements={requirements}
                errors={errors}
                availableExtras={availableExtras}
                countries={countries}
                onAutoComplete={onAutoComplete}
                forceSelection={forceSelection}
                isAuthenticated={isAuthenticated}
                onUseOriginDetails={onUseOriginDetails}
                usableOriginDetails={usableOriginDetails}
              />
            </P2GFade>
          )}
          {view === AddressView.Edit && (
            <P2GFade in>
              <AddressEdit
                type={type}
                disabledFields={disabledFields}
                section={section}
                requirements={requirements}
                errors={errors}
                availableExtras={availableExtras}
                countries={countries}
                forceSelection={forceSelection}
                onUseOriginDetails={onUseOriginDetails}
                usableOriginDetails={usableOriginDetails}
              />
            </P2GFade>
          )}
          {view === AddressView.Saved && (
            <P2GFade in>
              <AddressSaved
                isoCode={section.data?.country}
                onSelected={onSelectedSavedAddress}
                selectedId={Number(section.data?.metadata?.id || '')}
                onChangeView={(view: AddressView) => setView(view)}
                errors={errors}
                type={type}
              />
            </P2GFade>
          )}
        </Suspense>
      </P2GGrid>
      {showExtrasAndCustoms && requirements.rules?.some((x) => AddressCustomsFields.includes(x.key)) && (
        <AdditionalFields
          type={type}
          unfilteredRequirementKeys={unfilteredRequirementKeys}
          requirements={requirements}
          errors={errors}
          loading={section.loading}
          section={section}
          wrapper={(x) => (
            <P2GGrid item xs={12}>
              {x}
            </P2GGrid>
          )}
        />
      )}
      {links?.length > 0 && (
        <P2GGrid item xs={12}>
          <P2GBox mt={usableOriginDetails?.length ? 2 : 0}>
            <P2GLinkGroup links={links} />
          </P2GBox>
        </P2GGrid>
      )}
      {showExtrasAndCustoms && (
        <Extras
          limitToExtras={type === BasketSections.ORIGIN ? OriginUpsells : DestinationUpsells}
          includeServiceUpgrade={type === BasketSections.DESTINATION}
          wrapper={(x) => (
            <P2GGrid item xs={12}>
              <P2GBox mt={2} dataTestId={`extras-section-${type}`}>
                {x}
              </P2GBox>
            </P2GGrid>
          )}
        />
      )}
    </P2GGrid>
  );
}
